import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../Components/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../Components/Card';
import FadeIn from '../Animations/FadeIn';
import { useCart } from '../DataService/CartContext';
import { useUser } from '../DataService/UserContext';
import { CheckoutContactInfo, CheckoutState } from '../Types/CartTypes';
import './CartPage.css';

const emptyContact: CheckoutContactInfo = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  postalCode: '',
};

export const CartPage = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { items, removeItem, clearCart, totalAmount } = useCart();
  const [contact, setContact] = useState<CheckoutContactInfo>(() => ({
    ...emptyContact,
    email: user?.email ?? '',
  }));

  React.useEffect(() => {
    if (!user) return;

    setContact(prev => ({
      firstName: prev.firstName || user.firstName || '',
      lastName: prev.lastName || user.lastName || '',
      email: prev.email || user.email || '',
      phone: prev.phone || user.phone || '',
      address: prev.address || user.address || '',
      city: prev.city || user.city || '',
      postalCode: prev.postalCode || user.postalCode || '',
    }));
  }, [user]);

  const payableItems = useMemo(() => items.filter(item => item.type !== 'booking'), [items]);

  const bookingOnlyItems = useMemo(() => items.filter(item => item.type === 'booking'), [items]);

  const isContactComplete = Object.values(contact).every(value => value.trim().length > 0);

  const canContinue = payableItems.length <= 1 && isContactComplete;

  const handleChange = (field: keyof CheckoutContactInfo, value: string) => {
    setContact(prev => ({ ...prev, [field]: value }));
  };

  const handleContinue = () => {
    if (!canContinue) return;

    const state: CheckoutState = {
      items,
      contact,
      totalAmount,
    };

    navigate('/payment', { state });
  };

  return (
    <div className="cart-page">
      <FadeIn>
        <h1>Varukorg</h1>
        <p className="cart-page-subtitle">Kontrollera ditt köp innan du går vidare till betalning.</p>
      </FadeIn>

      <div className="cart-page-grid">
        <Card disableHover>
          <CardHeader>
            <CardTitle>Kontaktuppgifter</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="cart-field-grid">
              {([
                ['firstName', 'Förnamn'],
                ['lastName', 'Efternamn'],
                ['email', 'E-post'],
                ['phone', 'Telefon'],
                ['address', 'Adress'],
                ['postalCode', 'Postnummer'],
                ['city', 'Stad'],
              ] as const).map(([field, label]) => (
                <label
                  key={field}
                  className={`cart-field ${['address'].includes(field) ? 'cart-field--wide' : ''}`}
                >
                  <span>{label}</span>
                  <input
                    value={contact[field]}
                    onChange={(e) => handleChange(field, e.target.value)}
                    placeholder={label}
                    type={field === 'email' ? 'email' : field === 'phone' ? 'tel' : 'text'}
                    className="cart-input"
                  />
                </label>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card disableHover>
          <CardHeader>
            <CardTitle>Din order</CardTitle>
          </CardHeader>
          <CardContent>
            {items.length === 0 ? (
              <p>Korgen är tom.</p>
            ) : (
              <div className="cart-order-list">
                {items.map(item => (
                  <div key={item.id} className="cart-order-item">
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <div>
                        <strong>{item.title}</strong>
                        <div className="cart-order-item-meta">{item.type}</div>
                        {item.description && <div className="cart-order-item-desc" style={{ marginTop: 4 }}>{item.description}</div>}
                        {Array.isArray(item.metadata?.slots) && (
                          <div className="cart-order-item-slots" style={{ marginTop: 4 }}>
                            {(item.metadata?.slots as string[]).join(', ')}
                          </div>
                        )}
                      </div>
                      <button onClick={() => removeItem(item.id)} className="cart-remove-btn">
                        Ta bort
                      </button>
                    </div>
                    <div style={{ marginTop: 8 }}>Pris: {item.price} kr</div>
                  </div>
                ))}
              </div>
            )}

            <div className="cart-summary-block">
              <div className="cart-summary-row">
                <span>Totalt</span>
                <strong>{totalAmount} kr</strong>
              </div>

                {payableItems.length > 1 && (
                  <p className="cart-summary-note" style={{ color: 'hsl(var(--primary))' }}>
                    Du kan ha en köpbar produkt i korgen åt gången. Bokningar kan ligga kvar samtidigt.
                  </p>
                )}

              {payableItems.length === 0 && bookingOnlyItems.length > 0 && (
                <p className="cart-summary-note" style={{ color: 'hsl(var(--accent))' }}>
                  Korgen innehåller bara inkluderade bokningar, så ingen betalning krävs.
                </p>
              )}

              <div className="cart-actions">
                <Button text="Rensa korg" variant="secondary" onClick={clearCart} />
                <Button text="Fortsätt till betalning" onClick={handleContinue} disabled={!canContinue} />
              </div>
              {!isContactComplete && (
                <p className="cart-summary-note" style={{ color: 'hsl(var(--muted-foreground))', marginTop: 12 }}>
                  Fyll i alla kontaktuppgifter för att fortsätta till betalning.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
